import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import type { GuestbookEntry } from '@brandonstrong/types';
import { Notice, Section } from '../components/Section';
import { api, ApiClientError } from '../lib/api';
import { getRecaptchaToken, preloadRecaptcha } from '../lib/recaptcha';
import { usePageMeta } from '../lib/usePageMeta';
import { SEO } from '../seo/pages';

const PAGE_SIZE = 20;
const field = 'input-night';

/** "Sep 9, 2026" for the meta line under a name. */
const postedOn = (iso: string): string =>
  new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(
    new Date(iso),
  );

function Entry({ entry, fresh }: { entry: GuestbookEntry; fresh?: boolean }) {
  return (
    <article className={`border-t border-white/10 py-8 first:border-t-0 ${fresh ? 'rise-in' : ''}`}>
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h2 className="text-xl sm:text-2xl">{entry.name}</h2>
        <p className="text-meta text-sm">{postedOn(entry.createdAt)}</p>
      </header>
      <p className="mt-3 max-w-3xl whitespace-pre-line text-ink-200">{entry.message}</p>
    </article>
  );
}

/**
 * Every message goes to the API, which runs it past the AI moderator before
 * it is stored. An approved message appears at the top of the wall at once;
 * a rejected one comes back as a 422 whose message is the moderator's own
 * sentence to the writer, shown next to the form so they can fix it and try
 * again.
 */
function MessageForm({ onPosted }: { onPosted: (entry: GuestbookEntry) => void }) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [rejection, setRejection] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; message?: string }>({});
  const nameId = useId();
  const messageId = useId();

  useEffect(() => {
    preloadRecaptcha();
  }, []);

  const submit = useMutation({
    mutationFn: async () => {
      const recaptchaToken = await getRecaptchaToken('guestbook');
      return api.guestbook.submit({ name: name.trim(), message: message.trim(), recaptchaToken });
    },
    onSuccess: (entry) => {
      setName('');
      setMessage('');
      setRejection(null);
      setFailure(null);
      setFieldErrors({});
      onPosted(entry);
    },
    onError: (error: unknown) => {
      setRejection(null);
      setFailure(null);
      if (error instanceof ApiClientError) {
        if (error.code === 'message_rejected') {
          setRejection(error.message);
          return;
        }
        if (error.status === 400 && error.details) {
          setFieldErrors({
            name: error.details.name?.[0],
            message: error.details.message?.[0] ?? error.details.recaptchaToken?.[0],
          });
          if (error.details.recaptchaToken) setFailure(error.message);
          return;
        }
        setFailure(error.message);
        return;
      }
      setFailure('Something went wrong sending your message. Please try again.');
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const errors: { name?: string; message?: string } = {};
    if (name.trim().length < 2) errors.name = 'Please tell us your name';
    if (message.trim().length < 10) errors.message = 'Please write at least a sentence';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    submit.mutate();
  };

  const remaining = 2000 - message.length;

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      <label htmlFor={nameId} className="flex flex-col gap-1 text-sm">
        <span className="text-meta">Your name</span>
        <input
          id={nameId}
          className={field}
          autoComplete="name"
          maxLength={80}
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-invalid={Boolean(fieldErrors.name)}
        />
        {fieldErrors.name && <span className="text-xs text-danger-400">{fieldErrors.name}</span>}
      </label>
      <label htmlFor={messageId} className="flex flex-col gap-1 text-sm">
        <span className="text-meta">Your message</span>
        <textarea
          id={messageId}
          className={`${field} min-h-40`}
          maxLength={2000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          aria-invalid={Boolean(fieldErrors.message)}
          placeholder="A memory, a story, or what you would say to him."
        />
        <span className="flex justify-between text-xs">
          {fieldErrors.message ? (
            <span className="text-danger-400">{fieldErrors.message}</span>
          ) : (
            <span />
          )}
          {remaining < 200 && <span className="text-ink-400">{remaining} left</span>}
        </span>
      </label>

      {rejection && (
        <Notice tone="danger">
          <p className="font-sans font-semibold">This message cannot be posted as written.</p>
          <p className="mt-1">{rejection}</p>
        </Notice>
      )}
      {failure && <Notice tone="warning">{failure}</Notice>}

      <div className="flex flex-wrap items-center gap-4">
        <button type="submit" className="btn-primary" disabled={submit.isPending}>
          {submit.isPending ? 'Checking…' : 'Post message'}
        </button>
        <p className="text-sm text-ink-400">
          Messages are checked before they post. Keep it kind, and skip the links.
        </p>
      </div>
    </form>
  );
}

export function GuestbookPage() {
  usePageMeta(SEO.guestbook.title, SEO.guestbook.description, { jsonLd: SEO.guestbook.jsonLd });
  const qc = useQueryClient();
  const [justPosted, setJustPosted] = useState<GuestbookEntry[]>([]);

  const wall = useInfiniteQuery({
    queryKey: ['guestbook'],
    queryFn: ({ pageParam, signal }) => api.guestbook.list(pageParam, PAGE_SIZE, signal),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    staleTime: 30_000,
  });

  const pages = wall.data?.pages ?? [];
  const accepting = pages[0]?.acceptingMessages ?? true;
  const fetched = pages.flatMap((p) => p.items);
  // A message posted this visit sits at the top of the wall from the moment
  // the API accepts it. Until the list refetches, the server total does not
  // know about it, so it is added to the count; once the refetch includes it,
  // it is counted by the server and only kept out of the list below to avoid
  // a duplicate.
  const fetchedIds = new Set(fetched.map((e) => e.id));
  const notYetFetched = justPosted.filter((e) => !fetchedIds.has(e.id)).length;
  const total = (pages[0]?.total ?? 0) + notYetFetched;
  const seen = new Set(justPosted.map((e) => e.id));
  const entries = fetched.filter((e) => !seen.has(e.id));

  const onPosted = (entry: GuestbookEntry) => {
    setJustPosted((list) => [entry, ...list]);
    void qc.invalidateQueries({ queryKey: ['guestbook'] });
  };

  return (
    <Section eyebrow="Guestbook" title="Leave a message">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
        <div>
          <p className="max-w-md text-ink-200">
            For Brandon, and for the family. Share a story, a memory, or what you would say to him.
            Everything posted here is read by the people closest to him.
          </p>
          <div className="mt-8">
            {accepting ? (
              <MessageForm onPosted={onPosted} />
            ) : (
              <Notice>The guestbook is not taking new messages right now. Check back soon.</Notice>
            )}
          </div>
        </div>

        <div>
          {justPosted.length > 0 && (
            <div className="mb-6">
              <Notice tone="success">Posted. Thank you for writing.</Notice>
            </div>
          )}
          {wall.isLoading ? (
            <p className="text-ink-400">Loading messages…</p>
          ) : wall.isError ? (
            <Notice tone="warning">The messages could not be loaded. Please refresh.</Notice>
          ) : entries.length === 0 && justPosted.length === 0 ? (
            <p className="text-ink-400">No messages yet. Be the first to write one.</p>
          ) : (
            <>
              <p className="text-meta text-sm">
                {total} {total === 1 ? 'message' : 'messages'}
              </p>
              <div className="mt-2">
                {justPosted.map((e) => (
                  <Entry key={e.id} entry={e} fresh />
                ))}
                {entries.map((e) => (
                  <Entry key={e.id} entry={e} />
                ))}
              </div>
              {wall.hasNextPage && (
                <button
                  type="button"
                  className="btn-outline mt-6"
                  onClick={() => void wall.fetchNextPage()}
                  disabled={wall.isFetchingNextPage}
                >
                  {wall.isFetchingNextPage ? 'Loading…' : 'Show more'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </Section>
  );
}
